import React, { useRef, useEffect, useState, useCallback } from 'react';
import * as THREE from 'three';
import { gsap } from 'gsap';
import './MouseZoneBackground.css';

const IMAGES = [
  '/media/SplitSlideShow/Vague50.jpg',
  '/media/SplitSlideShow/Anhydran10.jpg',
  '/media/SplitSlideShow/Anhydran50.jpg',
  '/media/SplitSlideShow/AnhydranMin1.jpg',
  '/media/SplitSlideShow/DSC_2167.JPG',
  '/media/SplitSlideShow/eveil.jpg',
  '/media/SplitSlideShow/exportsphere_12screenshots254.jpg',
  '/media/SplitSlideShow/FN00236415_51202.jpg',
  '/media/SplitSlideShow/image (0).png',
  '/media/SplitSlideShow/image (27).png',
  '/media/SplitSlideShow/image (40).png',
  '/media/SplitSlideShow/image (69).png',
  '/media/SplitSlideShow/image (72).png',
  '/media/SplitSlideShow/image (73).png',
  '/media/SplitSlideShow/img1-1.jpg',
  '/media/SplitSlideShow/img1-2.jpg',
  '/media/SplitSlideShow/phonemes2pousch2.jpg',
  '/media/SplitSlideShow/phonemes2pousch3.jpg',
  '/media/SplitSlideShow/PhotosDNSEP_lum_6726.JPG',
  '/media/SplitSlideShow/PhotosDNSEP_lum_6728.JPG',
  '/media/SplitSlideShow/PhotosDNSEP_lum_6750.JPG',
  '/media/SplitSlideShow/PhotosDNSEP_lum_6830.JPG',
  '/media/SplitSlideShow/PhotosDNSEP_lum_6835.JPG',
  '/media/SplitSlideShow/PhotosDNSEP_lum_6854.JPG',
  '/media/SplitSlideShow/Secheresse20.jpg',
  '/media/SplitSlideShow/SoGlitch10EV.jpg',
  '/media/SplitSlideShow/test3d.jpg',
  '/media/SplitSlideShow/tinafond.jpg',
  '/media/SplitSlideShow/Transmutation_11.jpg',
  '/media/SplitSlideShow/Transmutation_visual.jpg',
];

// Extrait le nom du fichier sans extension ni chemin
const getLabel = (path) => {
  const filename = path.split('/').pop();
  return filename.replace(/\.[^.]+$/, '');
};

const LABELS = IMAGES.map(getLabel);

const DISPLACEMENTS = [
  '/media/1.png',
  '/media/2.png',
  '/media/10.png',
  '/media/11.png',
  '/media/13.png',
];

const COLS = 12;
const ROWS = 8;
const N    = IMAGES.length;

// Caractères utilisés pour le scramble — mélange alphanumérique + symboles
const CHARS = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789#@%&·—';

function buildGrid(cols, rows, n) {
  const grid = [];
  for (let r = 0; r < rows; r++) {
    for (let c = 0; c < cols; c++) {
      const base  = Math.floor((c / cols + r / rows) * n * 0.7);
      const noise = (c * 3 + r * 7) % n;
      grid.push((base + noise) % n);
    }
  }
  return grid;
}

const GRID = buildGrid(COLS, ROWS, N);

function getCoverAB() { return { a1: 1, a2: 1 }; }
function getContainSize(cW, cH, imgR) {
  return (cW / cH > imgR) ? { w: cH * imgR, h: cH } : { w: cW, h: cW / imgR };
}
function getStretchAB(cW, cH, imgR) {
  const cr = cW / cH;
  return (cr > imgR) ? { a1: cr / imgR, a2: 1 } : { a1: 1, a2: imgR / cr };
}

// ── Hook scramble text ────────────────────────────────────────
function useScramble() {
  const frameRef   = useRef(null);
  const iterRef    = useRef(0);
  const targetRef  = useRef('');
  const [display, setDisplay] = useState('');

  const scramble = useCallback((text) => {
    if (frameRef.current) cancelAnimationFrame(frameRef.current);
    targetRef.current = text;
    iterRef.current   = 0;
    const totalFrames = text.length * 3; // vitesse : 3 frames par lettre

    const tick = () => {
      const target = targetRef.current;
      const iter   = iterRef.current;
      const result = target
        .split('')
        .map((char, i) => {
          if (char === ' ') return ' ';
          // Les lettres déjà "résolues" (avant le front de révélation)
          if (i < Math.floor(iter / 3)) return char;
          // Les lettres encore en scramble
          return CHARS[Math.floor(Math.random() * CHARS.length)];
        })
        .join('');

      setDisplay(result);
      iterRef.current++;

      if (iterRef.current <= totalFrames) {
        frameRef.current = requestAnimationFrame(tick);
      } else {
        setDisplay(target);
      }
    };

    frameRef.current = requestAnimationFrame(tick);
  }, []);

  useEffect(() => () => {
    if (frameRef.current) cancelAnimationFrame(frameRef.current);
  }, []);

  return { display, scramble };
}

// ─────────────────────────────────────────────────────────────
const MouseZoneBackground = () => {
  const containerRef = useRef(null);
  const sceneRef     = useRef(null);
  const { display, scramble } = useScramble();

  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    const W = container.offsetWidth;
    const H = container.offsetHeight;

    const renderer = new THREE.WebGLRenderer({ antialias: false, alpha: true });
    renderer.setPixelRatio(window.devicePixelRatio);
    renderer.setSize(W, H);
    Object.assign(renderer.domElement.style, {
      position: 'absolute', inset: '0',
      width: '100%', height: '100%',
    });
    container.appendChild(renderer.domElement);

    const scene  = new THREE.Scene();
    const camera = new THREE.OrthographicCamera(-W/2, W/2, H/2, -H/2, 1, 1000);
    camera.position.z = 1;
    const render = () => renderer.render(scene, camera);

    const loader = new THREE.TextureLoader();
    loader.crossOrigin = 'anonymous';

    const dispTextures = DISPLACEMENTS.map(src => {
      const t = loader.load(src, render);
      t.magFilter = t.minFilter = THREE.LinearFilter;
      t.wrapS = t.wrapT = THREE.RepeatWrapping;
      return t;
    });

    const imageRatios = new Array(N).fill(1);
    const textures = IMAGES.map((src, i) => {
      const t = loader.load(src, (tex) => {
        imageRatios[i] = tex.image.width / tex.image.height;
        render();
      });
      t.magFilter = t.minFilter = THREE.LinearFilter;
      // ClampToEdge : les UV hors [0,1] affichent du transparent (bandes noires contain)
      t.wrapS = t.wrapT = THREE.ClampToEdgeWrapping;
      return t;
    });

    const { a1, a2 } = getCoverAB(W, H, 1);

    const material = new THREE.ShaderMaterial({
      uniforms: {
        texture1:   { value: textures[0] },
        texture2:   { value: textures[0] },
        disp:       { value: dispTextures[0] },
        dispFactor: { value: 1.0 },
        angle1:     { value: Math.PI / 4 },
        angle2:     { value: 3 * -Math.PI / 4 },
        intensity1: { value: 0.4 },
        intensity2: { value: 0.4 },
        res:        { value: new THREE.Vector4(W, H, a1, a2) },
        res2:       { value: new THREE.Vector4(W, H, a1, a2) },
        meshSize:      { value: new THREE.Vector2(W, H) },
        containerSize: { value: new THREE.Vector2(W, H) },
      },
      vertexShader: `
        varying vec2 vUv;
        void main() {
          vUv = uv;
          gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
        }
      `,
      fragmentShader: `
        varying vec2 vUv;
        uniform float dispFactor;
        uniform sampler2D disp;
        uniform sampler2D texture1;
        uniform sampler2D texture2;
        uniform float angle1;
        uniform float angle2;
        uniform float intensity1;
        uniform float intensity2;
        uniform vec4 res;
        uniform vec4 res2;
        uniform vec2 meshSize;
        uniform vec2 containerSize;

        mat2 getRotM(float angle) {
          float s = sin(angle);
          float c = cos(angle);
          return mat2(c, -s, s, c);
        }

        void main() {
          vec2 uvContainer = (vUv - 0.5) * (meshSize / containerSize) + 0.5;
          vec2 uv1cover = (vUv - 0.5) * vec2(res.z,  res.w)  + 0.5;
          vec2 uv2cover = (vUv - 0.5) * vec2(res2.z, res2.w) + 0.5;
          vec4 dispTex  = texture2D(disp, uvContainer);
          vec2 dispVec  = vec2(dispTex.r, dispTex.g);
          vec2 uv1 = uv1cover + getRotM(angle1) * dispVec * intensity1 * dispFactor;
          vec2 uv2 = uv2cover + getRotM(angle2) * dispVec * intensity2 * (1.0 - dispFactor);
          vec4 t1  = texture2D(texture1, uv1);
          vec4 t2  = texture2D(texture2, uv2);
          gl_FragColor = mix(t1, t2, dispFactor);
        }
      `,
      transparent: true,
    });

    const bgMat = new THREE.ShaderMaterial({
      uniforms: {
        texture1:{value:textures[0]}, texture2:{value:textures[0]},
        disp:{value:dispTextures[0]}, dispFactor:{value:1.0},
        angle1:{value:Math.PI/4}, angle2:{value:3*-Math.PI/4},
        intensity1:{value:0.4}, intensity2:{value:0.4},
        res:{value:new THREE.Vector4(W,H,getStretchAB(W,H,1).a1,getStretchAB(W,H,1).a2)},
        res2:{value:new THREE.Vector4(W,H,getStretchAB(W,H,1).a1,getStretchAB(W,H,1).a2)},
      },
      vertexShader:`varying vec2 vUv;void main(){vUv=uv;gl_Position=projectionMatrix*modelViewMatrix*vec4(position,1.0);}`,
      fragmentShader:`
        varying vec2 vUv;uniform float dispFactor;
        uniform sampler2D disp,texture1,texture2;
        uniform float angle1,angle2,intensity1,intensity2;
        uniform vec4 res,res2;
        mat2 rotM(float a){float s=sin(a),c=cos(a);return mat2(c,-s,s,c);}
        void main(){
          vec2 dv=texture2D(disp,vUv).rg;
          vec2 u1=(vUv-.5)*vec2(res.z,res.w)+.5+rotM(angle1)*dv*intensity1*dispFactor;
          vec2 u2=(vUv-.5)*vec2(res2.z,res2.w)+.5+rotM(angle2)*dv*intensity2*(1.-dispFactor);
          gl_FragColor=mix(texture2D(texture1,u1),texture2D(texture2,u2),dispFactor);
        }`,
      transparent:true,
    });
    const bgMesh = new THREE.Mesh(new THREE.PlaneGeometry(W,H,1), bgMat);
    bgMesh.position.z = -0.1;
    scene.add(bgMesh);
    const initS = getContainSize(W,H,1);
    const mesh = new THREE.Mesh(new THREE.PlaneGeometry(initS.w,initS.h,1), material);
    scene.add(mesh);
    render();

    sceneRef.current = {
      material, bgMat, mesh, bgMesh, render, textures, dispTextures, imageRatios,
      currentIdx: 0, containerW: W, containerH: H,
    };

    const onResize = () => {
      const w = container.offsetWidth;
      const h = container.offsetHeight;
      renderer.setSize(w, h);
      camera.left=-w/2; camera.right=w/2; camera.top=h/2; camera.bottom=-h/2;
      camera.updateProjectionMatrix();
      const s = sceneRef.current;
      s.containerW=w; s.containerH=h;
      const imgR = s.imageRatios[s.currentIdx];
      const cs = getContainSize(w,h,imgR);
      s.mesh.geometry.dispose();
      s.mesh.geometry = new THREE.PlaneGeometry(cs.w,cs.h,1);
      s.material.uniforms.res.value.set(w,h,1,1);
      s.material.uniforms.res2.value.set(w,h,1,1);
      s.material.uniforms.meshSize.value.set(cs.w,cs.h);
      s.material.uniforms.containerSize.value.set(w,h);
      s.bgMesh.geometry.dispose();
      s.bgMesh.geometry = new THREE.PlaneGeometry(w,h,1);
      const sab = getStretchAB(w,h,imgR);
      s.bgMat.uniforms.res.value.set(w,h,sab.a1,sab.a2);
      s.bgMat.uniforms.res2.value.set(w,h,sab.a1,sab.a2);
      render();
    };
    window.addEventListener('resize', onResize);

    // Lance le scramble sur le premier label
    scramble(LABELS[0]);

    return () => {
      window.removeEventListener('resize', onResize);
      renderer.dispose();
      if (container.contains(renderer.domElement)) container.removeChild(renderer.domElement);
    };
  }, []);

  const handleMouseMove = (e) => {
    if (window.innerWidth <= 1024) return;
    const s = sceneRef.current;
    if (!s) return;

    const rect   = e.currentTarget.getBoundingClientRect();
    const x      = (e.clientX - rect.left) / rect.width;
    const y      = (e.clientY - rect.top)  / rect.height;
    const col    = Math.min(Math.floor(x * COLS), COLS - 1);
    const row    = Math.min(Math.floor(y * ROWS), ROWS - 1);
    const newIdx = GRID[row * COLS + col];

    if (newIdx === s.currentIdx) return;

    const w = s.containerW;
    const h = s.containerH;
    const ab1     = getCoverAB(w, h, s.imageRatios[s.currentIdx]);
    const ab2     = getCoverAB(w, h, s.imageRatios[newIdx]);
    const dispIdx = Math.floor(Math.random() * s.dispTextures.length);

    const csFrom = getContainSize(w,h,s.imageRatios[s.currentIdx]);
    const csTo   = getContainSize(w,h,s.imageRatios[newIdx]);
    s.material.uniforms.texture1.value=s.textures[s.currentIdx];
    s.material.uniforms.texture2.value=s.textures[newIdx];
    s.material.uniforms.disp.value=s.dispTextures[dispIdx];
    s.material.uniforms.res.value.set(w,h,1,1);
    s.material.uniforms.res2.value.set(w,h,1,1);
    s.material.uniforms.meshSize.value.set(csFrom.w,csFrom.h);
    s.material.uniforms.containerSize.value.set(w,h);
    s.material.uniforms.dispFactor.value=0;
    const sab1=getStretchAB(w,h,s.imageRatios[s.currentIdx]);
    const sab2=getStretchAB(w,h,s.imageRatios[newIdx]);
    s.bgMat.uniforms.texture1.value=s.textures[s.currentIdx];
    s.bgMat.uniforms.texture2.value=s.textures[newIdx];
    s.bgMat.uniforms.disp.value=s.dispTextures[dispIdx];
    s.bgMat.uniforms.res.value.set(w,h,sab1.a1,sab1.a2);
    s.bgMat.uniforms.res2.value.set(w,h,sab2.a1,sab2.a2);
    s.bgMat.uniforms.dispFactor.value=0;
    gsap.killTweensOf(s.material.uniforms.dispFactor);
    gsap.killTweensOf(s.bgMat.uniforms.dispFactor);
    const proxy1 = { w: csFrom.w, h: csFrom.h };
    gsap.to(proxy1, { w: csTo.w, h: csTo.h, duration: 1.2, ease: 'expo.out',
      onUpdate: () => {
        s.mesh.geometry.dispose();
        s.mesh.geometry = new THREE.PlaneGeometry(proxy1.w, proxy1.h, 1);
        s.material.uniforms.meshSize.value.set(proxy1.w, proxy1.h);
      }
    });
    gsap.to(s.material.uniforms.dispFactor,{value:1,duration:1.2,ease:'expo.out',onUpdate:s.render});
    gsap.to(s.bgMat.uniforms.dispFactor,{value:1,duration:1.2,ease:'expo.out'});

    // Scramble sur le nouveau label
    scramble(LABELS[newIdx]);

    s.currentIdx = newIdx;
  };

  useEffect(() => {
    const interval = setInterval(() => {
      if (window.innerWidth > 1024) return;
      const s = sceneRef.current;
      if (!s) return;
      let newIdx;
      do { newIdx = Math.floor(Math.random() * N); } while (newIdx === s.currentIdx);
      const w=s.containerW, h=s.containerH;
      const dispIdx=Math.floor(Math.random()*s.dispTextures.length);
      const csFrom=getContainSize(w,h,s.imageRatios[s.currentIdx]);
      const csTo=getContainSize(w,h,s.imageRatios[newIdx]);
      s.material.uniforms.texture1.value=s.textures[s.currentIdx];
      s.material.uniforms.texture2.value=s.textures[newIdx];
      s.material.uniforms.disp.value=s.dispTextures[dispIdx];
      s.material.uniforms.res.value.set(w,h,1,1);
      s.material.uniforms.res2.value.set(w,h,1,1);
      s.material.uniforms.meshSize.value.set(csFrom.w,csFrom.h);
      s.material.uniforms.containerSize.value.set(w,h);
      s.material.uniforms.dispFactor.value=0;
      const sab1=getStretchAB(w,h,s.imageRatios[s.currentIdx]);
      const sab2=getStretchAB(w,h,s.imageRatios[newIdx]);
      s.bgMat.uniforms.texture1.value=s.textures[s.currentIdx];
      s.bgMat.uniforms.texture2.value=s.textures[newIdx];
      s.bgMat.uniforms.disp.value=s.dispTextures[dispIdx];
      s.bgMat.uniforms.res.value.set(w,h,sab1.a1,sab1.a2);
      s.bgMat.uniforms.res2.value.set(w,h,sab2.a1,sab2.a2);
      s.bgMat.uniforms.dispFactor.value=0;
      gsap.killTweensOf(s.material.uniforms.dispFactor);
      gsap.killTweensOf(s.bgMat.uniforms.dispFactor);
      const proxy2={w:csFrom.w,h:csFrom.h};
      gsap.to(proxy2,{w:csTo.w,h:csTo.h,duration:1.2,ease:'expo.out',
        onUpdate:()=>{
          s.mesh.geometry.dispose();
          s.mesh.geometry=new THREE.PlaneGeometry(proxy2.w,proxy2.h,1);
          s.material.uniforms.meshSize.value.set(proxy2.w,proxy2.h);
        }
      });
      gsap.to(s.material.uniforms.dispFactor,{value:1,duration:1.2,ease:'expo.out',onUpdate:s.render});
      gsap.to(s.bgMat.uniforms.dispFactor,{value:1,duration:1.2,ease:'expo.out'});
      scramble(LABELS[newIdx]);
      s.currentIdx=newIdx;
    }, 4000);
    return () => clearInterval(interval);
  }, [scramble]);

  return (
    <div className="mzb-root" ref={containerRef} onMouseMove={handleMouseMove}>
      <div className="mzb-legend">
        <span className="mzb-legend-text">{display}</span>
      </div>
    </div>
  );
};

export default MouseZoneBackground;